import PropTypes from 'prop-types'
import React, { useEffect } from 'react'
import { useExpanded, useSortBy, useTable, useRowSelect } from 'react-table'

const FeaturedTable = ({
  data,
  columns,
  renderRowSubComponent = () => {},
  onColumnClick = () => {},
  onRowClick = () => {},
  onCellClick = () => {},
  onChangeSelectedRows = () => {},
  setClearSelectedRows = () => {},
  initialState = undefined,
  getRowId,
  getRowClassName = () => {},
}) => {
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    toggleAllRowsSelected,
    state: { selectedRowIds },
  } = useTable(
    {
      columns,
      data,
      initialState,
      autoResetSelectedRows: false,
      getRowId: getRowId || ((row) => row.id),
    },
    useSortBy,
    useExpanded,
    useRowSelect,
  )

  useEffect(() => {
    onChangeSelectedRows(Object.keys(selectedRowIds))
  }, [selectedRowIds])

  useEffect(
    () => setClearSelectedRows(() => () => toggleAllRowsSelected(false)),
    [toggleAllRowsSelected, setClearSelectedRows],
  )

  return (
    <div className={`ctp-featured-table`}>
      {data.length ? (
        <table {...getTableProps()}>
          <thead>
            {headerGroups.map((headerGroup) => {
              const { key, ...props } = headerGroup.getHeaderGroupProps()
              return (
                <tr {...props} key={key}>
                  {headerGroup.headers.map((column) => {
                    const { key, ...props } = column.getHeaderProps()
                    const customColumnProps = {}
                    if (onColumnClick) {
                      customColumnProps.onClick = () => onColumnClick(column)
                    }
                    return (
                      <th
                        key={key}
                        id={
                          typeof column.Header === 'string'
                            ? column.Header.replace(/ /g, '')
                            : column.id.replace(/ /g, '') || 'EmptyHeader'
                        }
                        {...props}
                        {...customColumnProps}
                      >
                        {column.render('Header')}
                      </th>
                    )
                  })}
                </tr>
              )
            })}
          </thead>
          <tbody {...getTableBodyProps()}>
            {rows.map((row) => {
              prepareRow(row)
              const { key, ...props } = row.getRowProps()
              const customProps = {}
              if (onRowClick) {
                customProps.onClick = () => onRowClick(row)
              }
              return (
                <React.Fragment key={key}>
                  <tr
                    {...props}
                    {...customProps}
                    className={[
                      onRowClick && 'clicable-row',
                      getRowClassName && getRowClassName(row),
                    ]
                      .filter(Boolean)
                      .join(' ')}
                  >
                    {row.cells.map((cell) => {
                      const customCellProps = {}
                      if (onCellClick) {
                        customCellProps.onClick = () => onCellClick(cell)
                      }

                      const { key, ...props } = cell.getCellProps()

                      return (
                        <td {...props} {...customCellProps} key={key}>
                          {cell.render('Cell', { row })}
                        </td>
                      )
                    })}
                  </tr>
                  {row.isExpanded && renderRowSubComponent ? renderRowSubComponent({ row }) : null}
                </React.Fragment>
              )
            })}
          </tbody>
        </table>
      ) : (
        <div className="ctp-featured-table empty">No results</div>
      )}
    </div>
  )
}

FeaturedTable.propTypes = {
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      Header: PropTypes.oneOfType([PropTypes.func, PropTypes.string]),
      Cell: PropTypes.oneOfType([PropTypes.func]),
      accessor: PropTypes.string,
    }),
  ),
  data: PropTypes.array,
  renderRowSubComponent: PropTypes.func,
  onColumnClick: PropTypes.func,
  onRowClick: PropTypes.func,
  onCellClick: PropTypes.func,
  onChangeSelectedRows: PropTypes.func,
  setClearSelectedRows: PropTypes.func,
  initialState: PropTypes.object,
  getRowId: PropTypes.func,
  getRowClassName: PropTypes.func,
}

export default FeaturedTable
